import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Loader2, Download } from "lucide-react";
import { api } from "../App";
import { toast } from "@/lib/toast";
import { formatMoney } from "@/lib/currency";

const CHART_COLORS = ["#396BE9","#62BF92","#F59E0B","#EF4444","#8B5CF6","#06B6D4","#EC4899","#84CC16","#6B7280"];

function getCatIconUrl(name) {
  const norm = (name || "").toLowerCase().trim();
  if (norm.includes("aliment") || norm.includes("comida")) return "/Iconos/Categorias/Alimnetacion.png";
  if (norm.includes("transporte") || norm.includes("carro") || norm.includes("moto") || norm.includes("uber")) return "/Iconos/Categorias/transporte.png";
  if (norm.includes("entretenimiento") || norm.includes("diversion") || norm.includes("cine")) return "/Iconos/Categorias/Entretenimiento.png";
  if (norm.includes("salud") || norm.includes("medica") || norm.includes("farmacia")) return "/Iconos/Categorias/salud.png";
  if (norm.includes("educac") || norm.includes("universidad") || norm.includes("colegio") || norm.includes("escuela") || norm.includes("curso")) return "/Iconos/Categorias/Educacion.png";
  if (norm.includes("hogar") || norm.includes("casa") || norm.includes("muebles")) return "/Iconos/Categorias/Hogar.png";
  if (norm.includes("servicios") || norm.includes("luz") || norm.includes("agua") || norm.includes("gas")) return "/Iconos/Categorias/Servicios.png";
  if (norm.includes("suscri") || norm.includes("netflix") || norm.includes("spotify")) return "/Iconos/Categorias/Suscripciones.png";
  if (norm.includes("viaje") || norm.includes("vuelo") || norm.includes("hotel")) return "/Iconos/Categorias/Viajes.png";
  if (norm.includes("deuda") || norm.includes("prestam") || norm.includes("banco")) return "/Iconos/Categorias/Deudas.png";
  if (norm.includes("ahorro") || norm.includes("meta") || norm.includes("piggy")) return "/Iconos/Categorias/Ahorros.png";
  if (norm.includes("deporte") || norm.includes("gym") || norm.includes("ejercicio") || norm.includes("futbol") || norm.includes("entreno")) return "/Iconos/Categorias/deporte.png";
  return "/Iconos/Categorias/otros.png";
}

function getCatIcon(icon, name="", size=24) {
  if (icon && typeof icon === "string" && (icon.startsWith("/") || icon.includes(".png"))) {
    return <img src={icon} alt={name || "category"} style={{ width: size, height: size, objectFit: "contain" }} />;
  }
  const target = name || icon || "";
  if (target && typeof target === "string" && (target.startsWith("/") || target.includes(".png"))) {
    return <img src={target} alt={name || "category"} style={{ width: size, height: size, objectFit: "contain" }} />;
  }
  const url = getCatIconUrl(target);
  return <img src={url} alt={target} style={{ width: size, height: size, objectFit: "contain" }} />;
}

const expenseCategory = (expense) => expense.trip_expense_category || expense.category || "Otros";

export default function TripHistoryPage() {
  const { tripId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [trip, setTrip] = useState(location.state?.trip || null);
  const [tripExpenses, setTripExpenses] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [downloading, setDownloading] = useState(false);
  
  const [hoveredCatHistory, setHoveredCatHistory] = useState(null);
  const [activeCatHistory, setActiveCatHistory] = useState(null);

  const selectCategory = (category) => {
    setActiveCatHistory(current => current?.name === category.name ? null : category);
  };

  useEffect(() => {
    setActiveCatHistory(null);
    setHoveredCatHistory(null);
  }, [tripId]);

  const handleDownload = async (format) => {
    try {
      setDownloading(true);
      const res = await api.get(`/trips/${tripId}/statement?format=${format}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `viaje_${tripId}.${format === 'pdf' ? 'pdf' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Reporte descargado');
    } catch (err) {
      console.error(err);
      toast.error('Error al descargar');
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    const fetchTripData = async () => {
      try {
        setLoadingHistory(true);
        // If we don't have the trip object from state, we fetch all trips and find it
        if (!trip) {
          const tripsRes = await api.get("/trips");
          const foundTrip = tripsRes.data.find(t => t.id.toString() === tripId);
          if (foundTrip) {
            setTrip(foundTrip);
          } else {
            toast.error("Viaje no encontrado");
            navigate("/dashboard/trips");
            return;
          }
        }
        
        // Fetch expenses
        const expensesRes = await api.get(`/trips/${tripId}/transactions`);
        setTripExpenses(expensesRes.data || []);
      } catch (err) {
        console.error(err);
        toast.error("Error al cargar los movimientos del viaje");
      } finally {
        setLoadingHistory(false);
      }
    };

    fetchTripData();
  }, [tripId, trip, navigate]);



  if (!trip) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <Loader2 className="w-10 h-10 text-primary animate-spin" />
      </div>
    );
  }

  // Calculations
  const historyCats = {};
  let totalHistorySpent = 0;
  (tripExpenses || []).forEach(exp => {
    const cat = expenseCategory(exp);
    const amt = Math.abs(exp.trip_amount ?? exp.amount ?? 0);
    historyCats[cat] = (historyCats[cat] || 0) + amt;
    totalHistorySpent += amt;
  });

  const sortedCats = Object.entries(historyCats).map(([name, amount], i) => ({
    name,
    amount,
    color: CHART_COLORS[i % CHART_COLORS.length],
    icon: name,
  })).sort((a, b) => b.amount - a.amount);

  const bgt = trip.budget || 0;
  const progress = bgt ? Math.min((totalHistorySpent / bgt) * 100, 100) : 0;
  const remaining = bgt > 0 ? Math.max(0, bgt - totalHistorySpent) : 0;
  const exceeded = bgt > 0 && totalHistorySpent > bgt ? totalHistorySpent - bgt : 0;

  const visibleExpenses = activeCatHistory
    ? tripExpenses.filter(exp => expenseCategory(exp) === activeCatHistory.name)
    : tripExpenses;

  const activeCatHistoryVal = activeCatHistory || sortedCats[0] || { name: "Sin gastos", amount: 0, icon: "📦" };
  const displayCatHistory = hoveredCatHistory || activeCatHistoryVal;

  return (
    <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto", width: "100%" }} className="aorra-trip-history-page">
      {/* Back Button */}
      <button
        onClick={() => navigate("/dashboard/trips")}
        style={{
          display: "flex", alignItems: "center", gap: "8px", background: "none", border: "none",
          cursor: "pointer", fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "14px",
          color: "#255E99", marginBottom: "20px"
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M15 18L9 12L15 6" stroke="#255E99" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        Volver a Mis Viajes
      </button>

      {/* Title & Header */}
      <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "16px", borderBottom: "1px solid #E0EDF8", paddingBottom: "16px", marginBottom: "20px", flexWrap: "wrap" }}>
        <div style={{
          width: "56px",
          height: "56px",
          background: "#F0FDFD",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}>
          {trip.icon && (trip.icon.startsWith("/") || trip.icon.includes(".png")) ? (
            <img src={trip.icon} alt="Icono" style={{ width: 32, height: 32, objectFit: "contain" }} />
          ) : (
            <span style={{ fontSize: 32 }}>{trip.icon || "🏖️"}</span>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <h2 style={{
            fontFamily: "'Manrope', sans-serif",
            fontWeight: 800,
            fontSize: "24px",
            lineHeight: "32px",
            color: "#1B2559",
            margin: 0,
          }}>Historial de {trip.name}</h2>
          <span style={{
            fontFamily: "'Manrope', sans-serif",
            fontWeight: 500,
            fontSize: "14px",
            color: "#687377",
          }}>Resumen analítico y movimientos del viaje</span>
        </div>
      </div>

      {/* Loader */}
      {loadingHistory ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "60px 0" }}>
          <Loader2 className="w-10 h-10 text-primary animate-spin" />
        </div>
      ) : (
        /* Grid Content */
        <div className="aorra-history-grid" style={{
          display: "grid",
          gridTemplateColumns: "1fr 1.2fr",
          gap: "32px",
          width: "100%",
          minWidth: 0,
        }}>

          {/* Left Column: Budget & Chart */}
          <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            
            {/* Budget Card */}
            <div style={{
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              padding: "20px",
              background: "#FFFFFF",
              border: "2px solid #CDF7F7",
              borderRadius: "16px",
              gap: "14px",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "15px", color: "#5C5C5C" }}>Presupuesto del Viaje</span>
                <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: "20px", color: "#255E99" }}>
                  {bgt > 0 ? `${trip.currency || 'COP'} ${bgt.toLocaleString()}` : "Sin límite"}
                </span>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 600, fontSize: "14px", color: "#8E8E8E" }}>Total Gastado</span>
                <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: "16px", color: exceeded > 0 ? "#FF0040" : "#4A4A4A" }}>
                  {trip.currency || 'COP'} {totalHistorySpent.toLocaleString()}
                </span>
              </div>

              {bgt > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 600, fontSize: "14px", color: "#8E8E8E" }}>
                    {exceeded > 0 ? "Excedido por" : "Presupuesto Restante"}
                  </span>
                  <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: "16px", color: exceeded > 0 ? "#FF0040" : "#62BF92" }}>
                    {trip.currency || 'COP'} {exceeded > 0 ? exceeded.toLocaleString() : remaining.toLocaleString()}
                  </span>
                </div>
              )}

              {/* Progress Bar */}
              {bgt > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "4px" }}>
                  <div style={{ position: "relative", width: "100%", height: "8px", background: "#E1E9FC", borderRadius: "4px", overflow: "hidden" }}>
                    <div style={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      height: "100%",
                      width: `${progress}%`,
                      background: exceeded > 0 ? "#FF0040" : "#82FFC3",
                      borderRadius: "4px",
                      transition: "width 0.4s ease",
                    }} />
                  </div>
                  <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "12px", color: "#687377", alignSelf: "flex-end" }}>
                    {progress.toFixed(0)}% consumido
                  </span>
                </div>
              )}
            </div>

            {/* Category Donut Chart Card */}
            <div style={{
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "24px 20px",
              background: "#FFFFFF",
              boxShadow: "-2px 0px 20px rgba(176, 179, 189, 0.15)",
              borderRadius: "20px",
              minHeight: "280px",
            }}>
              <h4 style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "16px", color: "#255E99", margin: "0 0 16px 0", alignSelf: "flex-start" }}>Gastos por Categoría</h4>
              
              {sortedCats.length === 0 ? (
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "40px 24px",
                  width: "100%",
                  boxSizing: "border-box"
                }}>
                  <svg viewBox="319 99 610 322" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: "220px", height: "auto", marginBottom: "8px" }}>
                    <path d="M655.608 181.119C666.541 181.119 675.404 189.988 675.404 200.929C675.404 211.869 666.541 220.739 655.608 220.739H836.601C847.533 220.739 856.396 229.608 856.396 240.549C856.396 251.489 847.533 260.359 836.601 260.359H898.816C909.749 260.359 918.612 269.228 918.612 280.169C918.612 291.11 909.749 299.979 898.816 299.979H845.084C834.151 299.98 825.288 308.848 825.288 319.789C825.288 330.73 834.151 339.6 845.084 339.6H862.052C872.985 339.6 881.849 348.468 881.849 359.409C881.849 370.35 872.985 379.219 862.052 379.219H714.996C713.54 379.219 712.12 379.062 710.754 378.764C709.387 379.062 707.968 379.219 706.512 379.219H449.164C438.231 379.219 429.368 370.35 429.368 359.409C429.368 348.468 438.231 339.6 449.164 339.6H338.872C327.939 339.6 319.076 330.73 319.076 319.789C319.076 308.848 327.939 299.98 338.872 299.979H451.992C462.925 299.979 471.788 291.11 471.788 280.169C471.788 269.228 462.925 260.359 451.992 260.359H381.292C370.359 260.359 361.496 251.49 361.496 240.549C361.496 229.608 370.359 220.739 381.292 220.739H494.412C483.479 220.739 474.616 211.869 474.616 200.929C474.616 189.988 483.479 181.119 494.412 181.119H655.608ZM910.128 339.6C921.061 339.6 929.924 348.468 929.924 359.409C929.924 370.35 921.061 379.219 910.128 379.219C899.195 379.219 890.332 370.35 890.332 359.409C890.332 348.468 899.195 339.6 910.128 339.6Z" fill="#F3F7FF"/>
                    <path fillRule="evenodd" clipRule="evenodd" d="M483.682 240.758H481.005C463.881 240.758 450 226.917 450 209.842C450 192.768 463.881 178.927 481.005 178.927C481.115 178.927 481.226 178.927 481.336 178.929C484.092 154.982 503.715 136.418 527.512 136.418C552.594 136.418 573.039 157.041 573.985 182.852C589.151 183.83 602.496 198.57 602.496 213.939C602.496 226.558 593.137 240.758 582.05 240.758H504.95" fill="white"/>
                    <path d="M483.682 240.758H481.005C463.881 240.758 450 226.917 450 209.842C450 192.768 463.881 178.927 481.005 178.927C481.115 178.927 481.226 178.927 481.336 178.929C484.092 154.982 503.715 136.418 527.512 136.418C552.594 136.418 573.039 157.041 573.985 182.852C589.151 183.83 602.496 198.57 602.496 213.939C602.496 226.558 593.137 240.758 582.05 240.758H504.95" stroke="#75A4FE" strokeWidth="2.5" strokeLinecap="round"/>
                    <path d="M645.298 109.469C699.175 109.469 742.888 153.913 742.888 208.782C742.888 225.873 735.727 247.833 721.207 274.729C706.701 301.598 684.919 333.272 655.836 369.757C655.242 370.502 654.573 371.181 653.842 371.785C648.025 376.585 639.486 375.684 634.761 369.757C605.677 333.272 583.895 301.598 569.39 274.729C554.869 247.833 547.708 225.873 547.708 208.782C547.708 153.913 591.421 109.469 645.298 109.469Z" fill="white" stroke="#1F64E7" strokeWidth="2.5"/>
                    <path d="M679.943 129.965C709.403 143.635 730.018 174.209 730.018 209.923C730.017 224.368 723.959 244.111 710.354 269.671C696.857 295.027 676.434 325.192 648.899 360.225C648.687 360.494 648.451 360.737 648.195 360.951C646.225 362.6 643.35 362.33 641.695 360.225C614.161 325.192 593.739 295.027 580.242 269.671C577.07 263.711 574.31 258.067 571.939 252.733C583.82 247.522 595.074 241.15 605.562 233.766C613.434 247.735 628.279 257.152 645.298 257.152C670.549 257.152 691.019 236.42 691.02 210.847C691.02 191.995 679.895 175.775 663.933 168.551C670.595 156.421 675.991 143.501 679.943 129.965Z" fill="#E8F0FE"/>
                    <path d="M596.665 363.25C597.345 363.129 597.797 362.479 597.676 361.8C597.554 361.12 596.905 360.668 596.225 360.789L596.445 362.02L596.665 363.25ZM576.786 365.328C576.125 365.526 575.749 366.222 575.947 366.883C576.144 367.545 576.841 367.921 577.502 367.723L577.144 366.526L576.786 365.328ZM565.904 371.871C566.539 371.598 566.832 370.863 566.559 370.229C566.286 369.594 565.551 369.301 564.917 369.574L565.411 370.722L565.904 371.871ZM695.846 361.058C695.167 360.932 694.515 361.38 694.388 362.058C694.262 362.737 694.71 363.39 695.389 363.516L695.617 362.287L695.846 361.058ZM596.445 362.02L596.225 360.789C589.17 362.05 582.645 363.577 576.786 365.328L577.144 366.526L577.502 367.723C583.257 366.003 589.689 364.497 596.665 363.25L596.445 362.02ZM565.411 370.722L564.917 369.574C558.884 372.168 554 375.098 550.606 378.309C547.21 381.522 545.208 385.114 545.208 388.992H546.458H547.708C547.708 386.039 549.223 383.059 552.324 380.125C555.426 377.191 560.019 374.401 565.904 371.871L565.411 370.722ZM546.458 388.992H545.208C545.208 393.855 548.346 398.261 553.48 402.07C558.638 405.897 566.022 409.291 575.033 412.119C593.071 417.78 617.913 421.262 645.298 421.262V420.012V418.762C618.095 418.762 593.517 415.3 575.782 409.734C566.907 406.948 559.816 403.658 554.97 400.062C550.101 396.449 547.708 392.694 547.708 388.992H546.458ZM645.298 420.012V421.262C672.683 421.262 697.525 417.78 715.563 412.119C724.574 409.291 731.958 405.897 737.116 402.07C742.25 398.261 745.388 393.855 745.388 388.992H744.138H742.888C742.888 392.694 740.495 396.449 735.626 400.062C730.78 403.658 723.689 406.948 714.814 409.734C697.079 415.3 672.501 418.762 645.298 418.762V420.012ZM744.138 388.992H745.388C745.388 382.556 739.918 376.913 731.363 372.303C722.706 367.638 710.436 363.772 695.846 361.058L695.617 362.287L695.389 363.516C709.845 366.205 721.835 370.009 730.177 374.504C738.621 379.055 742.888 384.063 742.888 388.992H744.138Z" fill="#1F64E7"/>
                    <path d="M645.298 163.051C671.123 163.051 692.056 183.957 692.056 209.741C692.055 235.526 671.123 256.431 645.298 256.431C619.473 256.431 598.54 235.525 598.54 209.741C598.54 183.957 619.473 163.051 645.298 163.051Z" stroke="#1F64E7" strokeWidth="2.5"/>
                    <path fillRule="evenodd" clipRule="evenodd" d="M763.523 130.532C762.608 128.247 760.794 126.437 758.506 125.523L746.962 120.911L758.506 116.298C760.794 115.384 762.608 113.574 763.523 111.289L768.142 99.7606L772.76 111.289C773.676 113.574 775.489 115.384 777.777 116.298L789.322 120.911L777.777 125.523C775.489 126.437 773.676 128.247 772.76 130.532L768.142 142.061L763.523 130.532Z" stroke="#75A4FE" strokeWidth="2.5" strokeLinejoin="round"/>
                    <path fillRule="evenodd" clipRule="evenodd" d="M709.376 336.64H707.426C694.949 336.64 684.834 326.54 684.834 314.08C684.834 301.62 694.949 291.52 707.426 291.52C707.507 291.52 707.587 291.52 707.667 291.521C709.676 274.047 723.974 260.5 741.314 260.5C759.59 260.5 774.487 275.549 775.177 294.385C786.228 295.098 795.952 305.854 795.952 317.069C795.952 326.278 789.132 336.64 781.053 336.64H724.874" fill="white"/>
                    <path d="M709.376 336.64H707.426C694.949 336.64 684.834 326.54 684.834 314.08C684.834 301.62 694.949 291.52 707.426 291.52C707.507 291.52 707.587 291.52 707.667 291.521C709.676 274.047 723.974 260.5 741.314 260.5C759.59 260.5 774.487 275.549 775.177 294.385C786.228 295.098 795.952 305.854 795.952 317.069C795.952 326.278 789.132 336.64 781.053 336.64H724.874" stroke="#75A4FE" strokeWidth="2.5" strokeLinecap="round"/>
                  </svg>
                  <p style={{
                    fontFamily: "'Manrope', sans-serif",
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "#687377",
                    margin: "12px 0 0 0",
                    textAlign: "center"
                  }}>No hay transacciones registradas</p>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
                  {/* SVG Chart */}
                  <div style={{ position: "relative", width: 180, height: 180, flexShrink: 0, marginBottom: "16px" }}>
                    <svg viewBox="0 0 100 100" style={{ width: "100%", height: "100%", transform: "rotate(-90deg)", overflow: "visible" }}>
                      <circle cx="50" cy="50" r="38" fill="none" stroke="#FFFFFF" strokeWidth="10" />
                      {(() => {
                        const count = sortedCats.length;
                        const circ = 2 * Math.PI * 38;
                        const gap = count > 1 ? 3 : 0;
                        const totalGap = count * gap;
                        const available = circ - totalGap;
                        return sortedCats.map((cat, i) => {
                          const pct = totalHistorySpent > 0 ? (cat.amount / totalHistorySpent) : 0;
                          const segment = Math.max(0.5, pct * available);
                          const dashArr = `${segment} ${circ}`;
                          const prev = sortedCats.slice(0, i).reduce((acc, c) => acc + (c.amount / Math.max(totalHistorySpent, 1)) * available, 0);
                          const dashOffset = -(prev + i * gap + gap / 2);
                          
                          const isSelected = displayCatHistory.name === cat.name;

                          return (
                            <circle
                              key={cat.name}
                              cx="50"
                              cy="50"
                              r="38"
                              fill="none"
                              stroke={isSelected ? "#82FFC3" : cat.color}
                              strokeWidth={hoveredCatHistory?.name === cat.name ? 13 : 10}
                              strokeDasharray={dashArr}
                              strokeDashoffset={dashOffset}
                              strokeLinecap="round"
                              style={{
                                opacity: hoveredCatHistory ? (hoveredCatHistory.name === cat.name ? 1 : 0.45) : 1,
                                transition: "all 0.25s",
                                cursor: "pointer",
                              }}
                              onMouseEnter={() => setHoveredCatHistory(cat)}
                              onMouseLeave={() => setHoveredCatHistory(null)}
                              onClick={() => selectCategory(cat)}
                            />
                          );
                        });
                      })()}
                    </svg>

                    {/* Center labels */}
                    <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", pointerEvents: "none" }}>
                      <div style={{
                        width: "34px",
                        height: "34px",
                        borderRadius: "50%",
                        background: "#E1E9FC",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "4px",
                      }}>
                        {getCatIcon(displayCatHistory.icon, displayCatHistory.name, 22)}
                      </div>
                      <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: "16px", color: "#000000", lineHeight: "22px", letterSpacing: "-0.5px" }}>
                        {formatMoney(displayCatHistory.amount, trip.currency || "COP")}
                      </span>
                      <span style={{
                        fontFamily: "'Manrope', sans-serif",
                        fontWeight: 700,
                        fontSize: "11px",
                        color: "#396BE9",
                        textTransform: "capitalize",
                        marginTop: "1px",
                      }}>
                        {displayCatHistory.name}
                      </span>
                    </div>
                  </div>

                  {/* Legend / Category list below chart */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "14px", width: "100%", marginTop: "24px" }}>
                    {sortedCats.map((cat, i) => {
                      const pct = totalHistorySpent > 0 ? (cat.amount / totalHistorySpent) * 100 : 0;
                      return (
                        <div
                          key={cat.name}
                          onClick={() => selectCategory(cat)}
                          onMouseEnter={() => setHoveredCatHistory(cat)}
                          onMouseLeave={() => setHoveredCatHistory(null)}
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            width: "100%",
                            gap: "12px",
                            cursor: "pointer",
                            padding: "8px 8px",
                            borderRadius: "12px",
                            background: activeCatHistory?.name === cat.name ? "#F4F7FF" : "transparent",
                            transition: "all 0.2s",
                            opacity: hoveredCatHistory ? (hoveredCatHistory.name === cat.name ? 1 : 0.4) : 1
                          }}
                        >
                          {/* Icon */}
                          <div style={{
                            width: "36px",
                            height: "36px",
                            borderRadius: "50%",
                            background: "#F4F7FF",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0
                          }}>
                            {getCatIcon(cat.icon, cat.name, 20)}
                          </div>
                          
                          <div style={{ display: "flex", flexDirection: "column", flex: 1, gap: "6px", marginLeft: "4px" }}>
                             <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                               <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: cat.color, flexShrink: 0 }} />
                               <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 600, fontSize: "14px", color: "#1B2559", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{cat.name}</span>
                             </div>
                             <div style={{ height: "6px", background: "#F4F7FF", borderRadius: "3px", overflow: "hidden", width: "100%" }}>
                               <div style={{ height: "100%", width: `${pct}%`, background: cat.color, borderRadius: "3px" }} />
                             </div>
                          </div>

                          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", flexShrink: 0, marginLeft: "12px", width: "80px" }}>
                             <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "14px", color: cat.color }}>{pct.toFixed(0)}%</span>
                             <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 500, fontSize: "12px", color: "#8F9BBA" }}>{formatMoney(cat.amount, trip.currency || "COP")}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Transactions List */}
          <div style={{ display: "flex", flexDirection: "column", background: "#FFFFFF", boxShadow: "-2px 0px 20px rgba(176, 179, 189, 0.1)", borderRadius: "20px", padding: "20px", boxSizing: "border-box" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <h4 style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "16px", color: "#255E99", margin: 0 }}>Movimientos Registrados</h4>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={() => handleDownload('excel')}
                  disabled={downloading || tripExpenses.length === 0}
                  style={{
                    display: "flex", alignItems: "center", gap: "6px",
                    padding: "6px 12px", borderRadius: "8px", border: "none",
                    background: "#E1E9FC", color: "#396BE9",
                    fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "12px",
                    cursor: downloading || tripExpenses.length === 0 ? "not-allowed" : "pointer",
                    opacity: downloading || tripExpenses.length === 0 ? 0.6 : 1
                  }}
                >
                  <Download size={14} /> EXCEL
                </button>
                <button
                  onClick={() => handleDownload('pdf')}
                  disabled={downloading || tripExpenses.length === 0}
                  style={{
                    display: "flex", alignItems: "center", gap: "6px",
                    padding: "6px 12px", borderRadius: "8px", border: "none",
                    background: "#FFF0F0", color: "#E74C3C",
                    fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "12px",
                    cursor: downloading || tripExpenses.length === 0 ? "not-allowed" : "pointer",
                    opacity: downloading || tripExpenses.length === 0 ? 0.6 : 1
                  }}
                >
                  <Download size={14} /> PDF
                </button>
              </div>
            </div>
            
            <div style={{ display: "flex", flexDirection: "column", gap: "12px", maxHeight: "420px", overflowY: "auto", paddingRight: "6px" }} className="custom-scrollbar">
              {tripExpenses.length === 0 ? (
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "60px 24px",
                  width: "100%",
                  boxSizing: "border-box"
                }}>
                  <svg viewBox="319 99 610 322" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: "220px", height: "auto", marginBottom: "8px" }}>
                    <path d="M655.608 181.119C666.541 181.119 675.404 189.988 675.404 200.929C675.404 211.869 666.541 220.739 655.608 220.739H836.601C847.533 220.739 856.396 229.608 856.396 240.549C856.396 251.489 847.533 260.359 836.601 260.359H898.816C909.749 260.359 918.612 269.228 918.612 280.169C918.612 291.11 909.749 299.979 898.816 299.979H845.084C834.151 299.98 825.288 308.848 825.288 319.789C825.288 330.73 834.151 339.6 845.084 339.6H862.052C872.985 339.6 881.849 348.468 881.849 359.409C881.849 370.35 872.985 379.219 862.052 379.219H714.996C713.54 379.219 712.12 379.062 710.754 378.764C709.387 379.062 707.968 379.219 706.512 379.219H449.164C438.231 379.219 429.368 370.35 429.368 359.409C429.368 348.468 438.231 339.6 449.164 339.6H338.872C327.939 339.6 319.076 330.73 319.076 319.789C319.076 308.848 327.939 299.98 338.872 299.979H451.992C462.925 299.979 471.788 291.11 471.788 280.169C471.788 269.228 462.925 260.359 451.992 260.359H381.292C370.359 260.359 361.496 251.49 361.496 240.549C361.496 229.608 370.359 220.739 381.292 220.739H494.412C483.479 220.739 474.616 211.869 474.616 200.929C474.616 189.988 483.479 181.119 494.412 181.119H655.608ZM910.128 339.6C921.061 339.6 929.924 348.468 929.924 359.409C929.924 370.35 921.061 379.219 910.128 379.219C899.195 379.219 890.332 370.35 890.332 359.409C890.332 348.468 899.195 339.6 910.128 339.6Z" fill="#F3F7FF"/>
                    <path fillRule="evenodd" clipRule="evenodd" d="M483.682 240.758H481.005C463.881 240.758 450 226.917 450 209.842C450 192.768 463.881 178.927 481.005 178.927C481.115 178.927 481.226 178.927 481.336 178.929C484.092 154.982 503.715 136.418 527.512 136.418C552.594 136.418 573.039 157.041 573.985 182.852C589.151 183.83 602.496 198.57 602.496 213.939C602.496 226.558 593.137 240.758 582.05 240.758H504.95" fill="white"/>
                    <path d="M483.682 240.758H481.005C463.881 240.758 450 226.917 450 209.842C450 192.768 463.881 178.927 481.005 178.927C481.115 178.927 481.226 178.927 481.336 178.929C484.092 154.982 503.715 136.418 527.512 136.418C552.594 136.418 573.039 157.041 573.985 182.852C589.151 183.83 602.496 198.57 602.496 213.939C602.496 226.558 593.137 240.758 582.05 240.758H504.95" stroke="#75A4FE" strokeWidth="2.5" strokeLinecap="round"/>
                    <path d="M645.298 109.469C699.175 109.469 742.888 153.913 742.888 208.782C742.888 225.873 735.727 247.833 721.207 274.729C706.701 301.598 684.919 333.272 655.836 369.757C655.242 370.502 654.573 371.181 653.842 371.785C648.025 376.585 639.486 375.684 634.761 369.757C605.677 333.272 583.895 301.598 569.39 274.729C554.869 247.833 547.708 225.873 547.708 208.782C547.708 153.913 591.421 109.469 645.298 109.469Z" fill="white" stroke="#1F64E7" strokeWidth="2.5"/>
                    <path d="M679.943 129.965C709.403 143.635 730.018 174.209 730.018 209.923C730.017 224.368 723.959 244.111 710.354 269.671C696.857 295.027 676.434 325.192 648.899 360.225C648.687 360.494 648.451 360.737 648.195 360.951C646.225 362.6 643.35 362.33 641.695 360.225C614.161 325.192 593.739 295.027 580.242 269.671C577.07 263.711 574.31 258.067 571.939 252.733C583.82 247.522 595.074 241.15 605.562 233.766C613.434 247.735 628.279 257.152 645.298 257.152C670.549 257.152 691.019 236.42 691.02 210.847C691.02 191.995 679.895 175.775 663.933 168.551C670.595 156.421 675.991 143.501 679.943 129.965Z" fill="#E8F0FE"/>
                    <path d="M596.665 363.25C597.345 363.129 597.797 362.479 597.676 361.8C597.554 361.12 596.905 360.668 596.225 360.789L596.445 362.02L596.665 363.25ZM576.786 365.328C576.125 365.526 575.749 366.222 575.947 366.883C576.144 367.545 576.841 367.921 577.502 367.723L577.144 366.526L576.786 365.328ZM565.904 371.871C566.539 371.598 566.832 370.863 566.559 370.229C566.286 369.594 565.551 369.301 564.917 369.574L565.411 370.722L565.904 371.871ZM695.846 361.058C695.167 360.932 694.515 361.38 694.388 362.058C694.262 362.737 694.71 363.39 695.389 363.516L695.617 362.287L695.846 361.058ZM596.445 362.02L596.225 360.789C589.17 362.05 582.645 363.577 576.786 365.328L577.144 366.526L577.502 367.723C583.257 366.003 589.689 364.497 596.665 363.25L596.445 362.02ZM565.411 370.722L564.917 369.574C558.884 372.168 554 375.098 550.606 378.309C547.21 381.522 545.208 385.114 545.208 388.992H546.458H547.708C547.708 386.039 549.223 383.059 552.324 380.125C555.426 377.191 560.019 374.401 565.904 371.871L565.411 370.722ZM546.458 388.992H545.208C545.208 393.855 548.346 398.261 553.48 402.07C558.638 405.897 566.022 409.291 575.033 412.119C593.071 417.78 617.913 421.262 645.298 421.262V420.012V418.762C618.095 418.762 593.517 415.3 575.782 409.734C566.907 406.948 559.816 403.658 554.97 400.062C550.101 396.449 547.708 392.694 547.708 388.992H546.458ZM645.298 420.012V421.262C672.683 421.262 697.525 417.78 715.563 412.119C724.574 409.291 731.958 405.897 737.116 402.07C742.25 398.261 745.388 393.855 745.388 388.992H744.138H742.888C742.888 392.694 740.495 396.449 735.626 400.062C730.78 403.658 723.689 406.948 714.814 409.734C697.079 415.3 672.501 418.762 645.298 418.762V420.012ZM744.138 388.992H745.388C745.388 382.556 739.918 376.913 731.363 372.303C722.706 367.638 710.436 363.772 695.846 361.058L695.617 362.287L695.389 363.516C709.845 366.205 721.835 370.009 730.177 374.504C738.621 379.055 742.888 384.063 742.888 388.992H744.138Z" fill="#1F64E7"/>
                    <path d="M645.298 163.051C671.123 163.051 692.056 183.957 692.056 209.741C692.055 235.526 671.123 256.431 645.298 256.431C619.473 256.431 598.54 235.525 598.54 209.741C598.54 183.957 619.473 163.051 645.298 163.051Z" stroke="#1F64E7" strokeWidth="2.5"/>
                    <path fillRule="evenodd" clipRule="evenodd" d="M763.523 130.532C762.608 128.247 760.794 126.437 758.506 125.523L746.962 120.911L758.506 116.298C760.794 115.384 762.608 113.574 763.523 111.289L768.142 99.7606L772.76 111.289C773.676 113.574 775.489 115.384 777.777 116.298L789.322 120.911L777.777 125.523C775.489 126.437 773.676 128.247 772.76 130.532L768.142 142.061L763.523 130.532Z" stroke="#75A4FE" strokeWidth="2.5" strokeLinejoin="round"/>
                    <path fillRule="evenodd" clipRule="evenodd" d="M709.376 336.64H707.426C694.949 336.64 684.834 326.54 684.834 314.08C684.834 301.62 694.949 291.52 707.426 291.52C707.507 291.52 707.587 291.52 707.667 291.521C709.676 274.047 723.974 260.5 741.314 260.5C759.59 260.5 774.487 275.549 775.177 294.385C786.228 295.098 795.952 305.854 795.952 317.069C795.952 326.278 789.132 336.64 781.053 336.64H724.874" fill="white"/>
                    <path d="M709.376 336.64H707.426C694.949 336.64 684.834 326.54 684.834 314.08C684.834 301.62 694.949 291.52 707.426 291.52C707.507 291.52 707.587 291.52 707.667 291.521C709.676 274.047 723.974 260.5 741.314 260.5C759.59 260.5 774.487 275.549 775.177 294.385C786.228 295.098 795.952 305.854 795.952 317.069C795.952 326.278 789.132 336.64 781.053 336.64H724.874" stroke="#75A4FE" strokeWidth="2.5" strokeLinecap="round"/>
                  </svg>
                  <p style={{
                    fontFamily: "'Manrope', sans-serif",
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "#687377",
                    margin: "12px 0 0 0",
                    textAlign: "center"
                  }}>No se han registrado movimientos</p>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  {visibleExpenses.map((exp, idx) => {

                    return (
                      <div key={idx} style={{
                        boxSizing: "border-box",
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "10px 0px",
                        gap: "8px",
                        width: "100%",
                        borderBottom: "1px solid #E0EDF8",
                        minWidth: 0,
                      }}>
                        {/* Left content (icon + description) */}
                        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexGrow: 1, minWidth: 0 }}>
                          <div style={{
                            width: "38px",
                            height: "38px",
                            borderRadius: "50%",
                            background: "#F6F8FD",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0
                          }}>
                            {getCatIcon(expenseCategory(exp), expenseCategory(exp), 20)}
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                            <span style={{
                              fontFamily: "'Manrope', sans-serif",
                              fontWeight: 700,
                              fontSize: "14px",
                              color: "#4A4A4A",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}>{exp.description || expenseCategory(exp)}</span>
                            <span style={{
                              fontFamily: "'Manrope', sans-serif",
                              fontWeight: 500,
                              fontSize: "12px",
                              color: "#8E8E8E",
                            }}>{expenseCategory(exp)}</span>
                          </div>
                        </div>
                        {/* Right content (date + amount) */}
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", flexShrink: 0, minWidth: 0, maxWidth: "120px" }}>
                          <span style={{
                            fontFamily: "'Manrope', sans-serif",
                            fontWeight: 500,
                            fontSize: "11px",
                            color: "#8E8E8E",
                            marginBottom: "2px",
                            whiteSpace: "nowrap",
                          }}>{new Date(exp.date || exp.created_at).toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                          <span style={{
                            fontFamily: "'Manrope', sans-serif",
                            fontWeight: 800,
                            fontSize: "14px",
                            color: "#FF0040",
                            whiteSpace: "nowrap",
                          }}>-{trip.currency || 'COP'} {Math.abs(exp.trip_amount ?? exp.amount).toLocaleString()}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

        </div>
      )}
      
      <style>{`
        @media (max-width: 768px) {
          .aorra-trip-history-page {
            padding: 12px !important;
          }
          .aorra-history-grid {
            grid-template-columns: 1fr !important;
            gap: 16px !important;
          }
          .aorra-history-grid > div {
            min-width: 0 !important;
            width: 100% !important;
            box-sizing: border-box !important;
          }
        }
        @media (max-width: 900px) {
          .aorra-history-grid {
            grid-template-columns: 1fr !important;
            gap: 20px !important;
          }
        }
      `}</style>
    </div>
  );
}
